import { Box, Button, Collapsible, VStack } from "@chakra-ui/react"
import Link from "@pathwright/ux/src/components/link/Link"
import { useId, useState } from "react"
import { useMeasure } from "react-use"
import { UseMeasureResult } from "react-use/lib/useMeasure"
import { HomeRegistrationFragment } from "@pathwright/graphql-client/src/generated"
import Pathicon from "../../../pathicon/Pathicon"
import { getRegistrationPathLink } from "../../utils"
import PathCardListItem from "./PathCardListItem"
import PathCard from "./RegistrationPathCard"

function getMaxH(size: UseMeasureResult<HTMLDivElement>[1]) {
  // Setting scrollable container to be max height of first two items.
  return size.height ? `${size.height}px` : 0
}

type PathCardComponent = typeof PathCard | typeof PathCardListItem

type PathCardSharedProps<
  TPathCardComponent extends PathCardComponent = PathCardComponent
> = {
  registration: HomeRegistrationFragment
  nestedRegistrations: HomeRegistrationFragment[]
  PathCardComponent: TPathCardComponent
} & Parameters<TPathCardComponent>[0]

type PathCardStackProps = {
  sizeRef: UseMeasureResult<HTMLDivElement>[0]
  size: UseMeasureResult<HTMLDivElement>[1]
} & PathCardSharedProps

function PathCardStack({
  registration,
  nestedRegistrations,
  PathCardComponent,
  size,
  sizeRef,
  ...pathCardProps
}: PathCardStackProps) {
  const maxH = getMaxH(size)

  return (
    <VStack w="100%">
      <VStack w="100%" maxH={maxH} overflow="auto">
        <VStack ref={sizeRef} w="100%">
          {nestedRegistrations.slice(0, 2).map((nestedRegistration, i) => (
            <PathCardComponent
              key={nestedRegistration.id}
              registration={nestedRegistration}
              parentRegistration={registration}
              {...pathCardProps}
            />
          ))}
        </VStack>
        {nestedRegistrations.slice(2).map((nestedRegistration, i) => (
          <PathCardComponent
            key={nestedRegistration.id}
            registration={nestedRegistration}
            parentRegistration={registration}
            {...pathCardProps}
          />
        ))}
      </VStack>
      <Button asChild colorPalette="whiteAlpha" cursor="pointer" w="100%">
        <Link
          to={getRegistrationPathLink(registration)}
          w="100%"
          variant="plain"
        >
          View all
          <Pathicon icon="chevron-right" />
        </Link>
      </Button>
    </VStack>
  )
}

function ExpandablePathCard({
  registration,
  nestedRegistrations,
  PathCardComponent,
  ...pathCardProps
}: PathCardSharedProps) {
  // Constrain the scrollable panel to the height of its first two paths.
  const [sizeRef, size] = useMeasure<HTMLDivElement>()
  const [open, setOpen] = useState(false)
  const contentId = useId()

  return (
    <Collapsible.Root
      open={open}
      ids={{ content: contentId }}
      unmountOnExit
      pos="relative"
      w="100%"
      zIndex={10}
      p={open ? 2 : 0}
      borderRadius={25}
      bg={open ? "blackAlpha.400" : undefined}
    >
      <PathCardComponent
        registration={registration}
        nestedRegistrations={nestedRegistrations}
        {...pathCardProps}
        clickTargetProps={{
          onClick: () => setOpen((open) => !open),
          "aria-label": `Show paths for ${registration.resource!.name}`,
          "aria-expanded": open,
          "aria-controls": contentId
        }}
      />
      <Collapsible.Content>
        <Box pt={2}>
          <PathCardStack
            registration={registration}
            nestedRegistrations={nestedRegistrations}
            PathCardComponent={PathCardComponent}
            sizeRef={sizeRef}
            size={size}
            {...pathCardProps}
          />
        </Box>
      </Collapsible.Content>
    </Collapsible.Root>
  )
}

function PathCardContainer({
  registration,
  nestedRegistrations,
  PathCardComponent,
  ...pathCardProps
}: PathCardSharedProps) {
  return nestedRegistrations.length ? (
    <ExpandablePathCard
      registration={registration}
      nestedRegistrations={nestedRegistrations}
      PathCardComponent={PathCardComponent}
      {...pathCardProps}
    />
  ) : (
    <PathCardComponent registration={registration} {...pathCardProps} />
  )
}

export default PathCardContainer
